import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import ForestCover from './ForestCover';
import FalconCardHeader from 'components/common/FalconCardHeader';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import cloudDownload from 'assets/img/icons/cloud-download.svg';
import Model from './Model';
import { FiInfo } from 'react-icons/fi';
import { isEmpty } from 'lodash';

const DownloadButton = ({ data, filename }) => {
  const handleDownload = () => {
    const title = filename;
    const currentDate = new Date();

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hour = String(currentDate.getHours()).padStart(2, '0');
    const minute = String(currentDate.getMinutes()).padStart(2, '0');
    const second = String(currentDate.getSeconds()).padStart(2, '0');

    const timestamp = `${year}-${month}-${day}-${hour}-${minute}-${second}`;
    const newFilename = `${title}_${timestamp}.csv`;

    const csvData = Papa.unparse({
      fields: ['name', 'value'],
      data: data.map(item => [item.name, item.value])
    });

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = newFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <OverlayTrigger overlay={<Tooltip id="nextTooltip">Download</Tooltip>}>
      <Button
        variant="light"
        size="sm"
        className="border-300 me-1 text-600"
        as={'a'}
        download
        onClick={handleDownload}
      >
        <img src={cloudDownload} alt="Download" width={15} />
      </Button>
    </OverlayTrigger>
  );
};

DownloadButton.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired, // Assuming data is an array of objects
  filename: PropTypes.string.isRequired
};

const ForestCoverMonitorChart = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(data?.variant[0]);
  const [showModal, setShowModal] = useState(false);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleYearChange = event => {
    setSelectedYear(event.target.value);
  };

  const forestCoverData = data?.data[selectedYear] || [];

  // Convert forestCoverData to match the expected format in ForestCover component
  const formattedForestCoverData = [
    { name: data?.series[0] || 'forest', value: forestCoverData[0] || 0 },
    { name: data?.series[1] || 'non_forest', value: forestCoverData[1] || 0 }
  ];

  return (
    <Card className={classNames('h-100', 'overflow-hidden')}>
      <FalconCardHeader
        title={data.label}
        titleTag="h5"
        light={true}
        desc={data?.desc}
        endEl={
          data?.variant?.length > 1 && (
            <Row className="align-items-center">
              <Col xs={8} className="ps-2 py-0">
                <Form.Select
                  className="ps-2 py-0 width"
                  value={selectedYear}
                  onChange={handleYearChange}
                  style={{ width: '100px', paddingRight: '20px' }}
                >
                  {data?.variant.map((year, index) => {
                    return (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col xs={4} className="ps-2 py-0">
                <DownloadButton
                  data={formattedForestCoverData}
                  filename="Forest_Cover"
                />
              </Col>
            </Row>
          )
        }
        infoBtn={
          !isEmpty(data.description) && <FiInfo onClick={handleShowModal} />
        }
      />
      <Card.Body className="p-0">
        <ForestCover
          data={formattedForestCoverData}
          areaName={data?.label || ''}
          radius={['100%', '70%']}
          series={data?.series}
        />
        <Card.Footer className="bg-white py-2 card-footer-style">
          <h5 className="infrence-info">{data?.info}</h5>
        </Card.Footer>
      </Card.Body>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={data.description}
        title={data?.label}
      />
    </Card>
  );
};

ForestCoverMonitorChart.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        data: PropTypes.array.isRequired
      })
    ),
    desc: PropTypes.string,
    label: PropTypes.string,
    series: PropTypes.arrayOf(PropTypes.string),
    info: PropTypes.string,
    description: PropTypes.object,
    variant: PropTypes.array
  })
};

export default ForestCoverMonitorChart;
